import { CommonModule, DatePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, Inject } from '@angular/core';
import { ReactiveFormsModule, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { BehaviorSubject, Observable, combineLatestWith, filter, map, takeUntil, withLatestFrom } from 'rxjs';
import { NgxsFormPluginModule, UpdateFormValue } from '@ngxs/form-plugin';
import { Select, ofActionSuccessful } from '@ngxs/store';
import { TranslateModule } from '@ngx-translate/core';

import { DataUpdateResponseWithAttachmentUrl, WebApiResponse, WebApiResponseBase } from '@app/shared/classes/general';
import { DatepickerNormalFormat, NgxsActionName, NgxsFormPath } from '@app/shared/constants';
import { AngularMaterialModule } from '@app/shared/modules/angular-material.module';
import { AppDataState } from '@app/shared/stores/app-data.state';
import { EditModalDialogBasedComponent } from '@shared/components/edit-modal-dialog/edit-modal-dialog.based.component';
import { SpinnerButtonComponent } from '@shared/components/spinner-button/spinner-button.component';
import * as AppDataAction from '@app/shared/actions/app-data.action';
import { KpiOthers } from '@app/shared/classes/kpi-others';
import { InjectDialogData } from '@shared/components/edit-modal-dialog/edit-modal-dialog.constant';
import { Kpi } from '@app/shared/classes/kpi';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { MAT_MOMENT_DATE_ADAPTER_OPTIONS, MomentDateAdapter } from '@angular/material-moment-adapter';
import { LogicalOperator } from '@app/shared/enums';
import { Driver } from '@shared/classes/driver';
import { DriverSearchGridComponent } from '@app/shared/components/driver-search-grid/driver-search-grid.component';
import { CommonDataState } from '@app/shared/stores/common-data.state';
import { UserProfile } from '@app/shared/classes/user-profile';

@Component({
  selector: 'app-others-edit-modal-dialog',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [CommonModule, SpinnerButtonComponent, TranslateModule, NgxsFormPluginModule, ReactiveFormsModule, MatDialogModule, AngularMaterialModule, DriverSearchGridComponent],
  templateUrl: './others-edit-modal-dialog.component.html',
  styleUrl: './others-edit-modal-dialog.component.css',
  providers: [
    DatePipe,
    {
      provide: DateAdapter,
      useClass: MomentDateAdapter,
      deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS],
    },
    { provide: MAT_DATE_FORMATS, useValue: DatepickerNormalFormat },
  ],
})
export class OthersEditModalDialogComponent extends EditModalDialogBasedComponent<OthersEditModalDialogComponent, KpiOthers>  {
  formPath = NgxsFormPath.KPI_OTHERS_EDIT_FORM;
  title = "";

  driverList$: Observable<Driver[]>;
  kpiSelectItems$: Observable<Kpi[]>;
  driverApexIDOrNameFilterSub = new BehaviorSubject<string>('');
  driverApexIDOrNameFilter$ = this.driverApexIDOrNameFilterSub.asObservable();

  @Select(AppDataState.taskResponse(NgxsActionName.GET_KPI_ALL)) kpiAllDataResponse$: Observable<WebApiResponse<Kpi[]>>;
  @Select(AppDataState.tasksExecuting([NgxsActionName.UPDATE_KPI_OTHERS, NgxsActionName.UPLOAD_ATTACHMENT], LogicalOperator.OR)) processing$: Observable<boolean>;
  @Select(AppDataState.taskError(NgxsActionName.UPDATE_KPI_OTHERS)) errorResponse$: Observable<WebApiResponseBase>;
  @Select(AppDataState.taskResponse(NgxsActionName.UPDATE_KPI_OTHERS)) successResponse$: Observable<DataUpdateResponseWithAttachmentUrl>;

  @Select(CommonDataState.profileMe) profileMe$: Observable<UserProfile>;
  
  constructor(public dialogRef: MatDialogRef<OthersEditModalDialogComponent>, @Inject(MAT_DIALOG_DATA) public data: InjectDialogData<KpiOthers>) {
    super();
    this.dataInit(dialogRef, data);
    this.isCreate = data.isCreate;
    this.initForm(data);
    this.title = data.isCreate ? "ml_144036" : "ml_143925";
  }

  initForm = (data: InjectDialogData<KpiOthers>) => {
    if (data.isCreate) {
      this.editForm = this.formBuilder.group({
        kpiOthers_Id: -1,
        kpi_Id: [null, Validators.required],
        driver_Id: -1,
        apexId: ["", Validators.required],
        driverName: ["", Validators.required],
        kpiTime: ["", Validators.required],
        title: ["", Validators.required],
        description: "",
        attachment: ""
      });
    }
    else {
      this.editForm = this.formBuilder.group({
        kpiOthers_Id: data.data.kpiOthers_Id,
        kpi_Id: [data.data.kpi_Id, Validators.required],
        driver_Id: data.data.driver_Id,
        apexId: [data.data.apexId, Validators.required],
        driverName: [data.data.driverName, Validators.required],
        kpiTime: [data.data.kpiTime, Validators.required],
        title: [data.data.title, Validators.required],
        description: data.data.description,
        attachment: data.data.attachment
      });
    }
  }

  override ngOnInit(): void {
    super.ngOnInit();

    // this.kpiSelectItems$ = this.kpiAllDataResponse$.pipe(filter(res => res !== undefined && res !== null && res.data !== undefined && res.data !== null),
    //   map(dataList => dataList.data.filter(item => item.kpiId.startsWith('T'))));

    this.kpiSelectItems$ = this.kpiAllDataResponse$.pipe(
      filter(res => res !== undefined && res !== null && res.data !== undefined && res.data !== null),
      map(dataList => dataList.data.filter(item => item.kpiId.startsWith('T')))
    ).pipe(
      combineLatestWith(this.profileMe$),
      map(([e1, e2]) => (e2?.country === 'CN'? e1 : e1.slice(0, 2)))
    );

    this.actions$.pipe(ofActionSuccessful(AppDataAction.UpdateKpiOthers), withLatestFrom(this.successResponse$, this.errorResponse$), takeUntil(this.destroy$)).subscribe(
      ([_, successResponse, errorResponse]) => this.processAfterUpdated(successResponse, errorResponse));
  }

  onDriverApexIDOrNameKeyUp = (event: any) => {
    this.driverApexIDOrNameFilterSub.next(event.target.value as string);
  }

  onDriverItemClicked = (event: any) => {
    var currentValue = { driver_Id: -1, apexId: '', driverName: '' };
    if (event.toggled) {
      var driver = event.item as Driver;
      currentValue.driver_Id = driver.driver_Id;
      currentValue.apexId = driver.apexId;
      currentValue.driverName = driver.fullName;
      this.driverApexIDOrNameFilterSub.next("");
    }

    this.store.dispatch(new UpdateFormValue({
      value: currentValue,
      path: this.formPath
    }));
  }

  onOkClick = () => {
    this.store.dispatch(new AppDataAction.UpdateKpiOthers());
  }

  override ngOnDestroy(): void {
    super.ngOnDestroy();
    this.driverApexIDOrNameFilterSub.complete();
  }
}
